import type { CampaignPeriodStatus } from '@/components/Functional/utils/campaignPeriod'
import { isMynaviMode } from '@/components/Functional/utils/campaignTypes'
import { Image } from '@/components/UI'
import { HtmlContent } from '@/components/UI/HtmlContent'
import type { CampaignData } from '@/schemas/v2/campaign'
import { mynaviTheme } from '@/theme/mynavi'

type OutOfTermPageProps = {
  periodStatus: Exclude<CampaignPeriodStatus, 'active'>
  campaignData: CampaignData | null
}

// オフセット付き ISO 文字列を実行環境のタイムゾーンに依存せず日本時間で整形するための formatter。
// new Date(value).getFullYear() 等は実行環境（SSR の Node が UTC 等）のローカルタイムゾーンに
// 引っ張られて日付が前日にずれるため、Asia/Tokyo 固定で日付パーツを抽出する。
const JST_DATE_FORMATTER = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

/** ISO 日付文字列を日本時間の "YYYY年M月D日" にフォーマット（MYNAVI 開始前メッセージ用） */
function formatCampaignStartAt(value: string): string {
  // ja-JP / long で "2026年6月26日" 形式の文字列が得られる
  return JST_DATE_FORMATTER.format(new Date(value))
}

/**
 * MYNAVI 開始前（before）に表示する専用メッセージ。
 * 「本キャンペーンは / {campaignStartAt}から開始します / 公開までしばらくお待ちください」を表示する。
 */
function MyNaviBeforeTermPage({ campaignData }: { campaignData: CampaignData | null }) {
  const campaignImage = campaignData?.image ?? null
  const startAt = campaignData?.campaignStartAt ?? null

  return (
    <main>
      {campaignImage && <Image src={campaignImage} fit="width-fit" />}
      <div
        className="mx-4 mt-8 flex flex-col items-center gap-1 text-center"
        style={{ fontFamily: mynaviTheme.fontFamily }}
      >
        <p className="m-0" style={{ fontSize: '16px', fontWeight: 700, color: mynaviTheme.primary }}>
          本キャンペーンは
        </p>
        {startAt && (
          <p className="m-0" style={{ fontSize: '16px', fontWeight: 700, color: mynaviTheme.primary }}>
            {formatCampaignStartAt(startAt)}から開始します
          </p>
        )}
        <p className="m-0 mt-2" style={{ fontSize: '14px', fontWeight: 400, color: mynaviTheme.bodyText }}>
          公開までしばらくお待ちください
        </p>
      </div>
    </main>
  )
}

/**
 * キャンペーン期間外（開始前 / 終了後）に表示するページコンポーネント。
 *
 * - MYNAVI モードかつ期間前（before）: 専用メッセージ（MyNaviBeforeTermPage）を表示
 * - 期間前（before）: campaignData.beforeTermMessageHtml を表示
 * - 期間後（after）:  campaignData.outOfTermMessageHtml を表示
 * - 各HTMLが未設定の場合はメッセージを表示しない（キャンペーントップ画像のみ表示）
 * - Header / BottomNavigation は表示しない
 */
export function OutOfTermPage({ periodStatus, campaignData }: OutOfTermPageProps) {
  if (isMynaviMode() && periodStatus === 'before') {
    return <MyNaviBeforeTermPage campaignData={campaignData} />
  }

  const messageHtml =
    periodStatus === 'before'
      ? (campaignData?.beforeTermMessageHtml ?? null)
      : (campaignData?.outOfTermMessageHtml ?? null)
  const campaignImage = campaignData?.image ?? null

  return (
    <main>
      {campaignImage && <Image src={campaignImage} fit="width-fit" />}
      {messageHtml && (
        <div className="mx-4 mt-6">
          <HtmlContent html={messageHtml} variant="body1" />
        </div>
      )}
    </main>
  )
}
